import { FormInput, FormLabel, Form, Button, Text, Alert } from '$components'
import { ElementIntent } from '$components'
import z from 'zod'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { invitationRegisterMutation } from '$api-client/@tanstack/react-query.gen'
import { RoutePath } from 'src/enums/routing'
import { useAuth } from '$features/auth/hooks/use-auth'
import { useInvitation } from '../components/invitation-provider'
import { InvitationFooter } from '../components/invitation-footer'
import { QueryErrorHelper } from '$utils/query'

export enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
  Email = 'email',
  Password = 'password',
  RepeatPassword = 'repeat_password',
}

const schema = z.object({
  first_name: z.string().min(1, 'First name is required').trim(),
  last_name: z.string().min(1, 'Last name is required').trim(),
  email: z.string().email('Invalid email address').trim(),
  password: z.string().min(8, 'Password must be at least 8 characters long').trim(),
  repeat_password: z.string().min(1, 'Password confirmation is required').trim(),
}).superRefine(({ password, repeat_password: repeatPassword }, ctx) => {
  if (password !== repeatPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Passwords do not match',
      path: [Input.RepeatPassword],
    })
  }
})

export type AcceptInvitationFormValues = z.infer<typeof schema>

export function InvitationAcceptPage() {
  const { invitation, token } = useInvitation()
  const { refetchUser } = useAuth()
  const navigate = useNavigate()

  const { mutate: register, isPending: isRegistering, error } = useMutation({
    ...invitationRegisterMutation(),
    onSuccess: () => {
      refetchUser()

      navigate(RoutePath.Root, { replace: true })
    },
  })

  const onSubmit = async (values: AcceptInvitationFormValues) => {
    register({
      path: {
        token,
      },
      body: {
        password: values.password,
        password_confirmation: values.password,
      },
    })
  }

  const maxTeamMembersError = QueryErrorHelper.getQueryErrorByCode(error, 'max_team_members')

  return (
    <div className="w-full h-screen flex items-center justify-center">
      <div className="flex flex-col w-full max-w-[420px]">
        <Text type="h1">
          Accept Invitation
        </Text>

        <Text
          type="p"
          className="mb-8 mt-2"
        >
          Almost there! Fill out the remaining information to create your account.
        </Text>

        <Form
          onSubmit={onSubmit}
          schema={schema}
          defaultValues={{
            [Input.FirstName]: invitation.data.first_name,
            [Input.LastName]: invitation.data.last_name,
            [Input.Email]: invitation.email,
          }}
        >
          {() => (
            <div className="flex flex-col gap-4">

              <div className="flex flex-col gap-2">
                <FormLabel htmlFor={Input.FirstName}>First Name</FormLabel>
                <FormInput
                  name={Input.FirstName}
                  disabled
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel htmlFor={Input.LastName}>Last Name</FormLabel>
                <FormInput
                  name={Input.LastName}
                  disabled
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel htmlFor={Input.Email}>Email</FormLabel>
                <FormInput
                  name={Input.Email}
                  disabled
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel htmlFor={Input.Password}>Create Password</FormLabel>
                <FormInput
                  name={Input.Password}
                  type="password"
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormLabel htmlFor={Input.RepeatPassword}>Confirm Password</FormLabel>
                <FormInput
                  name={Input.RepeatPassword}
                  type="password"
                />
              </div>

              { maxTeamMembersError && (
                <Alert intent={ElementIntent.Danger}>
                  <Alert.Heading>
                    Failed to create account
                  </Alert.Heading>

                  <Alert.Body>
                    { maxTeamMembersError.message }
                  </Alert.Body>
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                className="mt-4"
                isLoading={isRegistering}
                disabled={isRegistering}
              >
                Create Account
              </Button>
            </div>
          )}
        </Form>

        <InvitationFooter />
      </div>
    </div>
  )
}
